import {
  Fragment,
  type ReactElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  defaultPageConfig,
  emptyListConfig,
  type FormattingRule,
  type ViewMode,
} from '@saas/shared';
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  CalendarDays,
  ChevronDown,
  ChevronRight,
  Filter,
  KanbanSquare,
  LayoutGrid,
  List as ListIcon,
} from 'lucide-react';

import { Alert } from '@/components/Alert';
import { Button } from '@/components/Button';
import { Dropdown } from '@/components/Dropdown';
import { useTenantAuth } from '@/contexts/TenantAuthContext';
import { useSidebarSkin } from '@/hooks/useSidebarSkin';
import { isTemplateTableId } from '@/lib/module-templates/template-layouts';

import { FilterRowCell } from './filter-row/FilterRowCell';
import { clearActiveListLayout, setActiveListLayout } from './active-list-layout';
import { CalendarBoard } from './CalendarBoard';
import { CardsView } from './CardsView';
import { ColumnConfigModal } from './ColumnConfigModal';
import {
  type CfRulePreset,
  type ColumnContextAction,
  ColumnContextMenu,
} from './ColumnContextMenu';
import { ColumnHeaderFilterPopup } from './ColumnHeaderFilterPopup';
import { evaluateFormattingRules, precomputeRuleContext } from './formatting-evaluator';
import { FormattingRuleEditorModal } from './FormattingRuleEditorModal';
import { FormattingRulesManagerModal } from './FormattingRulesManagerModal';
import { passesHeaderFilter } from './header-filter';
import { KanbanBoard } from './KanbanBoard';
import {
  classifyListLoadError,
  isRetryableListLoadError,
  type ListLoadErrorKind,
} from './load-error';
import {
  buildOrGroupRelations,
  clearOrGroupPeers,
  computeOrGroupDisabled,
} from './or-group-disable';
import { compareCellValues } from './sort-utils';
import { TableEmpty } from './TableEmpty';
import { TableLoading } from './TableLoading';
import { TablePagination } from './TablePagination';
import type {
  ColumnConfig,
  ColumnDefinition,
  ColumnFilters,
  ColumnHeaderFilter,
  ColumnHeaderFilters,
  ConfigurableTableProps,
  RowActionsContext,
  TableLayout,
  TableLayoutConfig,
} from './types';
import { useTableConfig } from './useTableConfig';
import { useTableLayout } from './useTableLayout';

// Re-eksport kontrolek wiersza filtrów (przeniesione do `filter-row-controls.tsx`,
// task #9). Zachowany dla importów testów kolokowanych, które celują w
// `./ConfigurableTable` (DateFilter/BooleanFilter).
export {
  BooleanFilterRowControl,
  DateRangeFilterRowControl,
  MultiChoiceFilterRowControl,
  readBooleanFilterState,
  SearchFilterRowControl,
  SelectFilterRowControl,
  SingleChoiceFilterRowControl,
} from './filter-row-controls';

// Minimum column width in pixels
const MIN_COLUMN_WIDTH = 50;

// Kontekst akcji w kolumnie „Akcje" tabeli (drugi argument `renderActions`) — stały obiekt, żeby
// wołający nie dostawał nowego co render. Karty i Kanban mają własne (`CardsView`, `KanbanBoard`).
const TABLE_ACTIONS_CONTEXT: RowActionsContext = { placement: 'table' };

// ── Przypięta kolumna akcji (`stickyActionsColumn`) ─────────────────────────────────────────
// Cienka pionowa linia po LEWEJ stronie przypiętej komórki. Rysowana pseudo-elementem, nie
// obramowaniem komórki: tabela ma `border-collapse: collapse`, a obramowania komórek w tym trybie
// należą do tabeli i NIE jadą razem z komórką `sticky` (linia zostawałaby w miejscu przy przewijaniu).
// Kolor: w skórce `antd` (zawsze aktywnej) linie MIĘDZY WIERSZAMI rysuje `tr::after` stałym
// `#e6e6ec` (`index.css`, reguły skórki `antd`), a nie klasy `divide-*` z `<tbody>`. Pionowa linia
// używa tokenu `border-secondary` (w jasnej skórce `antd` = `#e6e6ec`, więc zgodna z liniami
// wierszy), w ciemnym `gray-700` (jak `dark:divide-gray-700` z `<tbody>`).
const PINNED_EDGE_CLASS = `after:pointer-events-none after:absolute after:inset-y-0 after:left-0 after:w-px after:bg-border-secondary after:content-[''] dark:after:bg-gray-700`;
// Nagłówek i wiersz filtrów: tło nagłówka (to samo co `thead`) — nieprzezroczyste.
const PINNED_HEADER_CELL_CLASS = `sticky right-0 bg-bg-tertiary dark:bg-gray-900 ${PINNED_EDGE_CLASS}`;
// Podsumowanie: tło `tfoot` — nieprzezroczyste.
const PINNED_FOOTER_CELL_CLASS = `sticky right-0 bg-bg-secondary ${PINNED_EDGE_CLASS}`;

/**
 * Czy `footerCells` dało dla kolumny jakąkolwiek treść: `null` / `undefined` / `false` / `''` to
 * brak (liczba 0 jest treścią).
 */
function hasFooterContent(content: React.ReactNode): boolean {
  return content !== null && content !== undefined && content !== false && content !== '';
}

/**
 * Komórka podsumowania w PIERWSZEJ kolumnie, gdy tabela ma `footerLabel` (np. „Razem"). Gdy
 * `footerCells` nie daje dla tej kolumny żadnej treści — sama etykieta (jak dotąd, bez opakowania).
 * Gdy daje (np. pierwszą widoczną kolumną jest kolumna kwot) — etykieta i ta treść RAZEM, etykieta
 * nad wartością: etykieta nie może zjadać sumy pierwszej kolumny.
 */
function FooterLabelCell({
  label,
  content,
}: {
  label: React.ReactNode;
  content: React.ReactNode;
}): ReactElement {
  if (!hasFooterContent(content)) return <>{label}</>;
  return (
    <>
      <div>{label}</div>
      <div>{content}</div>
    </>
  );
}
// Wiersz danych. Tło wiersza bywa PÓŁPRZEZROCZYSTE (pasek w trybie ciemnym), PRZEZROCZYSTE
// (wołający gasi podświetlenie przez `rowClassName`) albo ustawiane z reguł formatowania — a
// komórka przypięta musi zasłaniać przewijane pod nią kolumny, więc nie może prześwitywać.
// Dlatego NIE odtwarzamy kolorów wiersza po stronie komórki, tylko je DZIEDZICZYMY: komórka bierze
// obliczone tło `<tr>` (`background-color: inherit` — zwykłe, paskowane, zaznaczone, najechane,
// z `rowClassName`, z reguł formatowania) i maluje je na NIEPRZEZROCZYSTEJ bazie tbody (biały /
// `gray-800`). Kolejność warstw w komórce: kolor odziedziczony (dół) → baza (gradient jednolity)
// → ten sam kolor odziedziczony jeszcze raz (`::before` z ujemnym z-index, pod treścią komórki):
// wynik = kolor wiersza złożony na bazie, czyli dokładnie to, co widać na nieprzypiętej części wiersza.
// BEZ z-index na samej komórce: linia rozdzielająca wiersze (skórka tabeli, `tr::after`) ma
// zostać NAD przypiętą komórką, żeby nie miała przerwy.
const PINNED_BODY_CELL_CLASS = `sticky right-0 [background-color:inherit] bg-linear-to-b from-white to-white dark:from-gray-800 dark:to-gray-800 before:pointer-events-none before:absolute before:inset-0 before:-z-10 before:[background-color:inherit] before:content-[''] ${PINNED_EDGE_CLASS}`;

// Pomiar kontenera przewijania tabeli (przypięcie kolumny akcji + wiersze na całą szerokość).
interface TableViewport {
  /** Widoczna szerokość kontenera przewijania (`clientWidth`); 0 = jeszcze nie zmierzono. */
  width: number;
  /** Tabela jest szersza niż widoczna część (da się przewijać w bok). */
  overflowing: boolean;
  /** Kolumna akcji jest przypięta (opcja włączona i kolumna ≤ połowy `width`). */
  actionsPinned: boolean;
}

const INITIAL_TABLE_VIEWPORT: TableViewport = {
  width: 0,
  overflowing: false,
  actionsPinned: false,
};

// Tryby prezentacji listy w przełączniku nad tabelą (lista / karty / Kanban / kalendarz).
const VIEW_MODES: { mode: ViewMode; Icon: typeof ListIcon }[] = [
  { mode: 'list', Icon: ListIcon },
  { mode: 'cards', Icon: LayoutGrid },
  { mode: 'kanban', Icon: KanbanSquare },
  { mode: 'calendar', Icon: CalendarDays },
];

/**
 * Konfigurowalna tabela — port z SellCall.
 *
 * Adaptacje vs SellCall:
 *  - Auth: `useTenantAuth` zamiast `useAuth`. `isAdmin` z `me?.user.isSuperUser`
 *    (placeholder do Kroku 5 — tam doprecyzujemy permissioning UI-side). Poza scope
 *    tenanta (podgląd „na żywo" Kreatora u super admina) komponent musi być owinięty
 *    w `PreviewTenantAuthBoundary` — inaczej `useTenantAuth` rzuci (brak providera).
 *  - Bez multi-company switching column (`companies[]` / `activeCompanyId`
 *    — u nas inny mechanizm wyboru firmy w tenant scope).
 *  - Header filtry per kolumna (popup z 3 trybami: Wartości/Zakres/Warunek) —
 *    UI z `ColumnHeaderFilterPopup`. Bez external handlera filtry działają
 *    lokalnie na już pobranych rekordach (`applyHeaderFilter` w processedData);
 *    z external handlerem (`onHeaderFiltersChange`) callsite decyduje co
 *    z nimi zrobić (np. wysłać do backend).
 */
export function ConfigurableTable<T>({
  tableId,
  distinctTableId = tableId,
  columns,
  onVisibleColumnsChange,
  data,
  isLoading = false,
  isFetching = false,
  loadError,
  onRetryLoad,
  totalItems,
  page = 0,
  pageSize = 20,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions,
  sortColumn: externalSortColumn,
  sortDirection: externalSortDirection,
  onSort: externalOnSort,
  filterable = true,
  columnFilters: externalColumnFilters,
  onColumnFiltersChange,
  showOrdinal = false,
  selectable = false,
  selectedIds = [],
  onSelectionChange,
  getRowId = (row: T) => (row as { id: string }).id,
  onRowClick,
  onRowContextMenu,
  onRowUpdate,
  canMoveKanbanField,
  rowClassName,
  renderActions,
  actionsColumnWidth = 120,
  actionsColumnLabel,
  stickyActionsColumn = false,
  configurable = true,
  persistConfig = true,
  className = '',
  stickyHeader = true,
  striped = false,
  compact = false,
  bordered = false,
  emptyIcon,
  emptyTitle,
  emptyDescription,
  emptyAction,
  errorState,
  onClearAllFilters,
  renderBulkActions,
  headerFilters: externalHeaderFilters,
  onHeaderFiltersChange,
  viewMode: controlledViewMode,
  onViewModeChange,
  onAvailableViewModesChange,
  onCalendarRangeChange,
  showViewModeToggle = true,
  allowedViewModes,
  titleColumnId,
  previewLayoutConfig,
  testIdPrefix,
  fillHeight = false,
  wrapCells = false,
  groups,
  expandedGroupKeys,
  onToggleGroup,
  footerCells,
  footerLabel,
  toolbarExtra,
  listToolbar,
}: ConfigurableTableProps<T>) {
  const { t } = useTranslation();
  const { me } = useTenantAuth();
  const isAdmin = me?.user?.isSuperUser ?? false;

  // Tryb grupowany (opcjonalny, generyczny — patrz JSDoc `groups` w types.ts). Selekcja
  // i kolumna akcji DZIAŁAJĄ też w tym trybie (wspólne `renderDataRow`) — WYŁĄCZONE
  // niezależnie od przekazanych propów pozostają tylko wbudowany przełącznik widoku i
  // wbudowana paginacja (osobne ograniczenia, patrz JSDoc). Bez tego propa (`undefined`)
  // — `showSelectColumn`/`showActionsColumn` == `selectable`/`!!renderActions`, zero
  // zmian zachowania.
  const isGroupedMode = groups !== undefined;
  const showSelectColumn = selectable;
  const showActionsColumn = !!renderActions;
  // Kolumna „Lp." (liczba porządkowa) — tylko w trybie płaskim (w grupowanym numeracja per
  // koszyk nie ma sensu i wołający renderuje wiersze sam). Parytet z Figmą list.
  const showOrdinalColumn = showOrdinal && !isGroupedMode;
  // Lista wierszy do liczenia/ustawiania „zaznacz wszystko" — w trybie grupowanym `data`
  // bywa celowo puste (wołający renderuje z `groups`, patrz `MyPendingDecisionsTab`), więc
  // `processedData` (pochodna `data`) też byłoby puste; liczymy zamiast tego po spłaszczonej
  // liście wierszy ze WSZYSTKICH koszyków.
  const tableSkin = useSidebarSkin();
  const groupedRowsFlat = useMemo(() => groups?.flatMap((g) => g.rows) ?? [], [groups]);

  const [showConfigModal, setShowConfigModal] = useState(false);
  // Stan zwinięcia koszyków (tryb grupowany, WEWNĘTRZNY — używany tylko gdy wołający NIE
  // steruje zwinięciem z zewnątrz, patrz `isControlledExpand` niżej) — domyślnie wszystkie
  // rozwinięte (mirror dotychczasowego zachowania, bez regresji dla istniejących konsumentów).
  const [collapsedGroupKeys, setCollapsedGroupKeys] = useState<Set<string>>(new Set());
  const toggleGroupCollapse = useCallback((key: string) => {
    setCollapsedGroupKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);
  // Tryb STEROWANY zwinięcia (leniwe doładowanie wierszy po rozwinięciu, patrz JSDoc
  // `expandedGroupKeys` w types.ts) — WYMAGA obu propów naraz, inaczej klik nagłówka nie miałby
  // dokąd wysłać zdarzenia (samo `expandedGroupKeys` bez handlera) lub czyj stan czytać (samo
  // `onToggleGroup` bez źródła prawdy). Semantyka ODWROTNA do wewnętrznego `collapsedGroupKeys`:
  // klucz OBECNY = koszyk ROZWINIĘTY (nieobecny = domyślnie zwinięty).
  const isControlledExpand = expandedGroupKeys !== undefined && onToggleGroup !== undefined;
  const isGroupCollapsed = useCallback(
    (key: string): boolean =>
      isControlledExpand ? !expandedGroupKeys!.has(key) : collapsedGroupKeys.has(key),
    [isControlledExpand, expandedGroupKeys, collapsedGroupKeys],
  );
  const handleGroupToggleClick = useCallback(
    (key: string) => {
      if (isControlledExpand) {
        onToggleGroup!(key);
      } else {
        toggleGroupCollapse(key);
      }
    },
    [isControlledExpand, onToggleGroup, toggleGroupCollapse],
  );

  // Load/save layout configuration (przeniesione nad blok trybu widoku — `hasCalendarConfig`
  // z layoutu bramkuje kafelek „kalendarz" w przełączniku, więc layout musi być znany wcześniej).
  const {
    layout: fetchedLayout,
    isLoading: layoutLoading,
    availableLayouts,
    saveLayout,
    createLayout,
    deleteLayout,
    switchLayout,
    isSaving,
  } = useTableLayout(tableId, { enabled: persistConfig });

  // Podgląd „na żywo" Kreatora: draft `list_config` podany propem zastępuje
  // layout z serwera (persistConfig=false wyłącza fetch, a kanban/widgety/
  // domyślny tryb widoku żyją w layoutConfig — bez tego podgląd nie wie,
  // co klient realnie zobaczy). Stałe `id` = seeding defaultViewMode raz.
  const layout = useMemo<TableLayout | null>(() => {
    if (!previewLayoutConfig) return fetchedLayout;
    return {
      id: `preview-${tableId}`,
      tableId,
      name: 'preview',
      description: null,
      isDefault: true,
      layoutConfig: previewLayoutConfig,
      roleIds: null,
      createdAt: '',
      updatedAt: '',
    };
  }, [previewLayoutConfig, fetchedLayout, tableId]);

  // Zgłoszenie aktywnego widoku listy dla menu „Kreator ▾" (otwiera Kreator Listy na widoku
  // wybranym w przełączniku, nie na domyślnym) — patrz `active-list-layout.ts`. Tylko layout
  // z serwera (persistConfig), nigdy syntetyczny podgląd.
  const activeLayoutOwnerRef = useRef(Symbol('active-list-layout'));
  const activeLayoutId = persistConfig ? (fetchedLayout?.id ?? null) : null;
  useEffect(() => {
    if (!activeLayoutId) return;
    const owner = activeLayoutOwnerRef.current;
    setActiveListLayout(tableId, activeLayoutId, owner);
    return () => clearActiveListLayout(tableId, owner);
  }, [tableId, activeLayoutId]);

  // Kalendarz jako tryb widoku istnieje tylko, gdy layout ma skonfigurowane pole daty startu
  // (parytet z gatingiem Kanbana po `kanbanConfig`). Bez tego kafelek „kalendarz" nie pojawia się
  // w przełączniku (nie ma po czym rysować), a zapisany `defaultViewMode='calendar'` jest klampowany.
  const hasCalendarConfig = Boolean(layout?.layoutConfig?.calendarConfig?.startFieldSlug);

  // Tryb prezentacji listy (lista / karty / Kanban). Domyślnie sterowany
  // wewnętrznie (przełącznik w pasku tabeli) i synchronizowany z
  // `layout.layoutConfig.defaultViewMode` po załadowaniu. Gdy strona poda
  // `viewMode` + `onViewModeChange` (tryb sterowany — przełącznik w nagłówku
  // strony, parytet z modułami custom), źródłem prawdy jest prop.
  const [internalViewMode, setInternalViewMode] = useState<ViewMode>('list');
  const isViewModeControlled = controlledViewMode !== undefined;
  const viewMode = isViewModeControlled ? controlledViewMode : internalViewMode;

  // Ograniczenie dostępnych trybów widoku (opcjonalne, wsteczne — patrz JSDoc
  // `allowedViewModes` w types.ts). Bez propa: identyczne z VIEW_MODES (referencyjnie
  // ta sama stała), więc żadna z poniższych wartości nie zmienia zachowania.
  const visibleViewModes = useMemo(() => {
    const base =
      allowedViewModes === undefined
        ? VIEW_MODES
        : VIEW_MODES.filter(({ mode }) => allowedViewModes.includes(mode));
    // Kalendarz pokazujemy tylko, gdy skonfigurowany (parytet z gatingiem Kanbana).
    return base.filter(({ mode }) => mode !== 'calendar' || hasCalendarConfig);
  }, [allowedViewModes, hasCalendarConfig]);

  // Raport dostępnych trybów do strony (`onAvailableViewModesChange`) — przełącznik w nagłówku
  // (tryb sterowany) pokazuje dokładnie te pastylki, które tabela umie wyrenderować (m.in.
  // kalendarz tylko z `calendarConfig`). Porównanie TREŚCI z ostatnim raportem (ref), bo
  // `visibleViewModes` przelicza się przy zmianie layoutu — callback tylko na realną zmianę.
  const lastReportedViewModesRef = useRef<string | null>(null);
  useEffect(() => {
    if (!onAvailableViewModesChange) return;
    const modes = visibleViewModes.map(({ mode }) => mode);
    const key = modes.join(',');
    if (lastReportedViewModesRef.current === key) return;
    lastReportedViewModesRef.current = key;
    onAvailableViewModesChange(modes);
  }, [visibleViewModes, onAvailableViewModesChange]);

  // Tryb widoku używany DO RENDEROWANIA: gdy `viewMode` (z jakiegokolwiek źródła —
  // initial state, sync z zapisanego layoutu, zmiana usera) nie należy do dozwolonego
  // zbioru, klampujemy na pierwszy dozwolony. To wyliczenie, nie stan — żadnego
  // efektu korygującego, więc zero pętli/migotania. Surowy `viewMode`/`internalViewMode`
  // zostaje nietknięty (m.in. `buildListConfig` dalej zapisuje realną wartość ze stanu).
  // Przy pustym przecięciu (błędna konfiguracja propa) nie mamy czym klampować —
  // renderujemy surowy `viewMode` jak dotychczas.
  const effectiveViewMode = useMemo<ViewMode>(() => {
    // Gdy `viewMode` (initial / sync z layoutu / zmiana usera) nie należy do dozwolonego zbioru
    // (np. zapisany `calendar` bez konfiguracji, albo tryb spoza `allowedViewModes`), klampujemy
    // na pierwszy dozwolony. To wyliczenie, nie stan — surowy `viewMode` zostaje nietknięty.
    if (visibleViewModes.some((vm) => vm.mode === viewMode)) return viewMode;
    return visibleViewModes[0]?.mode ?? viewMode;
  }, [viewMode, visibleViewModes]);

  // Wbudowany przełącznik (lista/karty/Kanban) w pasku tabeli renderujemy tylko
  // gdy tryb NIE jest sterowany z zewnątrz, strona go nie wyłączyła, i jest >1
  // dozwolony tryb do przełączania (1 tryb = nic do przełączania). Moduły
  // custom mają własny przełącznik widoku w nagłówku → tu go chowamy.
  const showInternalViewModeToggle =
    !isViewModeControlled && showViewModeToggle && visibleViewModes.length > 1 && !isGroupedMode;

  // Local sorting state (used when no external handler provided)
  const [localSortColumn, setLocalSortColumn] = useState<string | null>(null);
  const [localSortDirection, setLocalSortDirection] = useState<'asc' | 'desc'>('asc');

  // Use external or local sorting
  const sortColumn = externalSortColumn ?? localSortColumn;
  const sortDirection = externalSortDirection ?? localSortDirection;
  const hasExternalSort = !!externalOnSort;

  // Local filter state (used when no external handler provided)
  const [localColumnFilters, setLocalColumnFilters] = useState<ColumnFilters>({});
  const columnFilters = externalColumnFilters ?? localColumnFilters;
  const hasExternalFilters = !!onColumnFiltersChange;

  // Handle sort change
  const handleSort = useCallback(
    (column: string, direction: 'asc' | 'desc') => {
      if (hasExternalSort) {
        externalOnSort!(column, direction);
      } else {
        setLocalSortColumn(column);
        setLocalSortDirection(direction);
      }
    },
    [hasExternalSort, externalOnSort],
  );

  // Handle filter change
  const handleFilterChange = useCallback(
    (columnId: string, value: string) => {
      const newFilters = { ...columnFilters, [columnId]: value || null };
      // Remove null/empty values
      Object.keys(newFilters).forEach((key) => {
        if (newFilters[key] === null || newFilters[key] === '') {
          delete newFilters[key];
        }
      });
      if (hasExternalFilters) {
        onColumnFiltersChange!(newFilters);
      } else {
        setLocalColumnFilters(newFilters);
      }
    },
    [columnFilters, hasExternalFilters, onColumnFiltersChange],
  );

  // Header filter popup state (per-column popup z 3 trybami)
  const [filterPopupColumnId, setFilterPopupColumnId] = useState<string | null>(null);
  const [filterPopupAnchorRect, setFilterPopupAnchorRect] = useState<{
    top: number;
    left: number;
    right: number;
    bottom: number;
  } | null>(null);
  const [localHeaderFilters, setLocalHeaderFilters] = useState<ColumnHeaderFilters>({});
  const headerFilters = externalHeaderFilters ?? localHeaderFilters;
  const hasExternalHeaderFilters = !!onHeaderFiltersChange;

  // ── Grupy OR (kolumny złożone) — wzajemne wygaszanie filtrów (czysta logika → `or-group-disable`).
  // Relacje z PEŁNEJ listy `columns` (nie tylko widocznych), by czyszczenie sąsiada działało też dla
  // pola składowego UKRYTEGO z aktywnym filtrem. `orGroupDisabled`: id kolumny → filtr grupy do echa.
  const orGroupRelations = useMemo(() => buildOrGroupRelations(columns), [columns]);
  const orGroupDisabled = useMemo(
    () => computeOrGroupDisabled(orGroupRelations, headerFilters),
    [orGroupRelations, headerFilters],
  );

  // Clear all filters — MUSI czyścić OBA mechanizmy (zwykłe columnFilters + popup
  // headerFilters). `hasActiveFilters` (niżej) liczy też headerFilters, więc przycisk
  // „Wyczyść filtry" w pustym stanie potrafi się pojawić wyłącznie z powodu aktywnego
  // headerFiltra (np. ekran filtrujący WYŁĄCZNIE przez popup, `filterable={false}`,
  // patrz `KsefInvoices.tsx`) — bez czyszczenia headerFilters klik nie robił nic
  // (dead end, finding [A] pre-commit review).
  //
  // `onClearAllFilters?.()` na końcu — dla callsite'ów z WŁASNYM stanem filtrowania
  // poza tabelą (np. AdminKartoteki.tsx: `searchPhrase`/`sourceFilter` zawężają
  // `data` PRZED przekazaniem do tabeli, więc tabela o nich nie wie i nie może ich
  // wyzerować sama) — bez tego callbacku wbudowany przycisk czyściłby tylko to, co
  // tabela widzi, zostawiając zewnętrzny filtr aktywnym (finding [A] pre-commit review).
  const clearAllFilters = useCallback(() => {
    if (hasExternalFilters) {
      onColumnFiltersChange!({});
    } else {
      setLocalColumnFilters({});
    }
    if (hasExternalHeaderFilters) {
      onHeaderFiltersChange!({});
    } else {
      setLocalHeaderFilters({});
    }
    onClearAllFilters?.();
  }, [
    hasExternalFilters,
    onColumnFiltersChange,
    hasExternalHeaderFilters,
    onHeaderFiltersChange,
    onClearAllFilters,
  ]);

  // Mutacja WYŁĄCZNIE stanu filtrów (bez zamykania popupu) — używana też przez
  // wiersz filtrów; pisanie w komórce nie może zamykać popupu otwartego gdzie indziej.
  const commitHeaderFilter = useCallback(
    (columnId: string, filter: ColumnHeaderFilter | null) => {
      const newFilters = { ...headerFilters };
      if (filter) {
        newFilters[columnId] = filter;
        // Wygaszanie wzajemne grup OR: zapis jednej strony (kolumna złożona ↔ pole składowe) czyści
        // drugą, inaczej sąsiad zostałby w stanie i doleciał do zapytania (podwójne filtrowanie).
        clearOrGroupPeers(newFilters, orGroupRelations, columnId);
      } else {
        delete newFilters[columnId];
      }
      if (hasExternalHeaderFilters) {
        onHeaderFiltersChange!(newFilters);
      } else {
        setLocalHeaderFilters(newFilters);
      }
    },
    [headerFilters, hasExternalHeaderFilters, onHeaderFiltersChange, orGroupRelations],
  );

  const handleHeaderFilterApply = useCallback(
    (columnId: string, filter: ColumnHeaderFilter | null) => {
      commitHeaderFilter(columnId, filter);
      setFilterPopupColumnId(null);
      setFilterPopupAnchorRect(null);
    },
    [commitHeaderFilter],
  );

  // Column resize state
  const [resizingColumn, setResizingColumn] = useState<string | null>(null);
  const [resizeStartX, setResizeStartX] = useState<number>(0);
  const [resizeStartWidth, setResizeStartWidth] = useState<number>(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const columnRefs = useRef<Map<string, HTMLTableCellElement>>(new Map());

  // Pomiar widocznej części tabeli — dwa zastosowania: (1) przypięcie kolumny akcji
  // (`stickyActionsColumn`, tylko gdy kolumna zajmuje najwyżej połowę widocznej szerokości),
  // (2) szerokość opakowań wierszy na całą szerokość (pusty stan, „Ładowanie…", nagłówek i stopka
  // grupy) — patrz `renderFullWidth`. Elementy trzymamy jako STAN (ref-callback), nie `useRef`:
  // tabela bywa montowana później (ładowanie layoutu) lub zastępowana kartami, a efekt pomiaru ma
  // dostać każdy nowy element.
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(null);
  const [tableElement, setTableElement] = useState<HTMLTableElement | null>(null);
  const [actionsHeaderElement, setActionsHeaderElement] = useState<HTMLTableCellElement | null>(
    null,
  );
  const [viewport, setViewport] = useState<TableViewport>(INITIAL_TABLE_VIEWPORT);
  const measureViewport = useCallback(() => {
    if (!scrollElement) return;
    const width = scrollElement.clientWidth;
    const overflowing = width > 0 && scrollElement.scrollWidth > width;
    // `offsetWidth`, nie `getBoundingClientRect().width`: ta sama miara układu co `clientWidth`
    // kontenera (obie całkowite, bez ułamków i bez przeliczeń transformacji), więc porównanie
    // „połowa widocznej szerokości" nie zależy od zaokrągleń ani od `transform: scale` przodków.
    const actionsWidth = actionsHeaderElement?.offsetWidth ?? 0;
    // Pusta/niezmierzona szerokość (0) nigdy nie przypina — dzielenie przez „połowę" zera dałoby
    // fałszywe „mieści się". Przypięcie nie zmienia układu (sticky), więc nie ma pętli pomiaru.
    const actionsPinned =
      stickyActionsColumn &&
      actionsHeaderElement !== null &&
      width > 0 &&
      actionsWidth <= width / 2;
    setViewport((prev) =>
      prev.width === width &&
      prev.overflowing === overflowing &&
      prev.actionsPinned === actionsPinned
        ? prev
        : { width, overflowing, actionsPinned },
    );
  }, [scrollElement, actionsHeaderElement, stickyActionsColumn]);
  useLayoutEffect(() => {
    if (!scrollElement) return undefined;
    measureViewport();
    if (typeof ResizeObserver === 'undefined') return undefined;
    // Kontener (zmiana rozmiaru okna/układu), tabela (dane, kolumny, ich szerokości) i nagłówek
    // kolumny akcji (zmiana treści komórek akcji) — każda z tych zmian może przełączyć stan.
    const observer = new ResizeObserver(measureViewport);
    observer.observe(scrollElement);
    if (tableElement) observer.observe(tableElement);
    if (actionsHeaderElement) observer.observe(actionsHeaderElement);
    return () => observer.disconnect();
  }, [scrollElement, tableElement, actionsHeaderElement, measureViewport]);
  const pinActionsColumn = stickyActionsColumn && showActionsColumn && viewport.actionsPinned;
  // Zawartość wierszy na całą szerokość (`colSpan`): przy tabeli szerszej niż kontener opakowanie
  // `sticky left-0` o szerokości WIDOCZNEJ części trzyma ją w oknie przewijania — pusty komunikat
  // nie ucieka za prawą krawędź, nagłówek grupy zostaje przy lewej krawędzi widocznej części.
  // Szerokość ustawiamy TYLKO przy przewijaniu w bok: gdy tabela się mieści, wygląd jest jak dotąd
  // (opakowanie wypełnia komórkę), a komórka `colSpan` nie dostaje dodatkowego wymogu szerokości
  // wpływającego na rozkład kolumn. Padding komórki przenosimy na opakowanie (`className`),
  // inaczej szerokość = widoczna część + padding wyszłaby poza widok.
  const renderFullWidth = (children: React.ReactNode, className = '') => (
    <div
      className={`sticky left-0 ${className}`}
      style={viewport.overflowing ? { width: viewport.width } : undefined}
      data-visible-width-wrapper=""
    >
      {children}
    </div>
  );

  // Manage column configuration state
  const { columnConfigs, setColumnConfigs, visibleColumns, resetColumnConfig, updateColumnWidth } =
    useTableConfig(columns, layout?.layoutConfig);

  // Menu kontekstowe nagłówka (Krok 7.2.1 — prawy klik na <th>).
  const [contextMenuColumnId, setContextMenuColumnId] = useState<string | null>(null);
  const [contextMenuAnchor, setContextMenuAnchor] = useState<{ x: number; y: number } | null>(null);

  // Rule Editor modal (Krok 7.2.3 — otwierany z submenu Conditional Formatting).
  const [ruleEditorColumnId, setRuleEditorColumnId] = useState<string | null>(null);
  const [ruleEditorPreset, setRuleEditorPreset] = useState<CfRulePreset | null>(null);

  // Manager Rules modal (Krok 7.2.4 — otwierany z submenu „Manage Rules").
  const [managerColumnId, setManagerColumnId] = useState<string | null>(null);

  // Lista reguł formatowania warunkowego (Krok 7.2.5) — synchronizowana z
  // layout.layoutConfig.formattingRules. Zmiany przez Editor/Manager → saveLayout.
  const [formattingRules, setFormattingRules] = useState<FormattingRule[]>([]);

  // Sync formattingRules z layout przy ładowaniu (jednorazowe; user może
  // zmieniać przez Manager modal i wtedy lokalny state + saveLayout).
  useEffect(() => {
    if (layout?.layoutConfig?.formattingRules) {
      setFormattingRules(layout.layoutConfig.formattingRules);
    }
  }, [layout?.id, layout?.layoutConfig?.formattingRules]);

  // Rozmiar strony z ustawień widoku listy (Kreator Listy → „Rekordów na stronę") — dla KAŻDEJ
  // listy z paginacją, nie tylko kartotek własnych (wcześniej listy systemowe zostawały przy
  // twardym 50). Stosowany przy pierwszym wczytaniu widoku i przy każdej zmianie ustawienia
  // (klucz = widok + wartość), więc zapis w kreatorze działa bez przeładowania. Ręczny wybór
  // w stopce zostaje do następnej zmiany ustawienia; wybór zrobiony ZANIM widok dojechał nie jest
  // nadpisywany przez pierwsze wczytanie.
  // Widok bez zapisanego ustawienia (albo lista bez żadnego widoku) → domyślne z kreatora
  // (`defaultPageConfig.pageSize`, 25) — lista pokazuje to samo, co kreator podpowiada w polu.
  // Czekamy, aż widok się wczyta, żeby nie przeskakiwać z domyślnej na zapisaną wartość.
  const layoutResolved = previewLayoutConfig ? true : persistConfig && !layoutLoading;
  const configuredPageSize =
    layout?.layoutConfig?.pageConfig?.pageSize ??
    (layoutResolved ? defaultPageConfig.pageSize : undefined);
  const appliedPageSizeKeyRef = useRef<string | null>(null);
  const userPickedPageSizeRef = useRef(false);
  useEffect(() => {
    if (!onPageSizeChange || configuredPageSize == null) return;
    const key = `${layout?.id ?? 'no-layout'}:${configuredPageSize}`;
    if (appliedPageSizeKeyRef.current === key) return;
    const firstApply = appliedPageSizeKeyRef.current === null;
    appliedPageSizeKeyRef.current = key;
    if (firstApply && userPickedPageSizeRef.current) return;
    if (configuredPageSize !== pageSize) onPageSizeChange(configuredPageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- tylko zmiana widoku/ustawienia
  }, [layout?.id, configuredPageSize]);
  const handleUserPageSizeChange = useCallback(
    (size: number) => {
      userPickedPageSizeRef.current = true;
      onPageSizeChange?.(size);
    },
    [onPageSizeChange],
  );
  // Presety stopki + rozmiar z widoku, jeśli spoza presetów — inaczej lista wyboru nie ma
  // pasującej opcji i pokazuje się pusta, choć zapytanie leci z poprawnym limitem.
  const effectivePageSizeOptions = useMemo(() => {
    const base = pageSizeOptions ?? [10, 20, 50, 100];
    return configuredPageSize && !base.includes(configuredPageSize)
      ? [...base, configuredPageSize].sort((a, b) => a - b)
      : base;
  }, [pageSizeOptions, configuredPageSize]);

  // Sync trybu widoku z zapisanego layoutu — jednorazowo per layout (ref
  // gwarantuje, że nasz własny zapis `defaultViewMode` nie cofnie wyboru usera
  // ani nie zresetuje stanu przy hot-reloadzie). W trybie sterowanym podnosimy
  // domyślny tryb do strony przez `onViewModeChange`.
  const seededLayoutRef = useRef<string | null>(null);
  useEffect(() => {
    const def = layout?.layoutConfig?.defaultViewMode;
    if (!def || !layout?.id) return;
    if (seededLayoutRef.current === layout.id) return;
    seededLayoutRef.current = layout.id;
    if (isViewModeControlled) {
      onViewModeChange?.(def);
    } else {
      setInternalViewMode(def);
    }
  }, [layout?.id, layout?.layoutConfig?.defaultViewMode, isViewModeControlled, onViewModeChange]);

  // Buduje pełny list_config do zapisu: zachowuje CAŁY istniejący layout
  // (kanbanConfig, pageConfig, buttonsConfig, customActionButtons, widgets,
  // defaultFilters, quickFilters) i nadpisuje tylko pola zarządzane przez tabelę
  // (kolumny / sort / reguły / domyślny tryb). Bez tego każdy auto-zapis
  // (zmiana trybu, resize, reguły) wymazywałby pola spoza tej czwórki — to był
  // root-cause znikania konfiguracji Kanbana po przełączeniu widoku na liście.
  const buildListConfig = useCallback(
    (overrides: Partial<TableLayoutConfig> = {}): TableLayoutConfig => ({
      ...(layout?.layoutConfig ?? emptyListConfig),
      columns: columnConfigs,
      defaultSort: sortColumn ? { column: sortColumn, direction: sortDirection || 'asc' } : null,
      formattingRules,
      defaultViewMode: viewMode,
      ...overrides,
    }),
    [layout?.layoutConfig, columnConfigs, sortColumn, sortDirection, formattingRules, viewMode],
  );

  // Tryb sterowany: przełącznik żyje w nagłówku strony i woła tylko
  // `onViewModeChange` (setState strony). Zapis domyślnego trybu do layoutu
  // robimy tutaj, gdy `viewMode` REALNIE się zmienił (ref z poprzedniego przebiegu)
  // i różni się od zapisanego. Strażnik równości chroni przed pętlą (po zapisie +
  // invalidate stają się równe). Strażnik zmiany chroni przed zapisem przy samym
  // załadowaniu layoutu: w tym przebiegu strona ma jeszcze początkowe 'list', a seed
  // wyżej dopiero podnosi zapisany domyślny tryb — bez strażnika tabela zapisywała
  // 'list' do layoutu i kolejny przebieg przywracał domyślny (dwa zbędne PUT-y na wejście).
  const prevControlledViewModeRef = useRef<ViewMode>(viewMode);
  useEffect(() => {
    const changed = prevControlledViewModeRef.current !== viewMode;
    prevControlledViewModeRef.current = viewMode;
    if (!changed) return;
    if (!isViewModeControlled || !persistConfig || !layout?.id) return;
    if (viewMode === layout.layoutConfig?.defaultViewMode) return;
    saveLayout(buildListConfig());
    // Zależność celowo zawężona do `viewMode` + tożsamości layoutu — zapis ma
    // następować na zmianę trybu, nie na każdą zmianę kolumn/formatowania.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    viewMode,
    isViewModeControlled,
    persistConfig,
    layout?.id,
    layout?.layoutConfig?.defaultViewMode,
  ]);

  // Helper — zapisz layout z aktualnymi formattingRules.
  const saveFormattingRules = useCallback(
    (rules: FormattingRule[]) => {
      setFormattingRules(rules);
      if (persistConfig) {
        saveLayout(buildListConfig({ formattingRules: rules }));
      }
    },
    [persistConfig, saveLayout, buildListConfig],
  );

  // Zmiana trybu widoku — aktualizuje stan i zapisuje jako domyślny tryb
  // w layoucie (preserwuje pozostałe pola list_config).
  const handleViewModeChange = useCallback(
    (mode: ViewMode) => {
      if (isViewModeControlled) {
        onViewModeChange?.(mode);
      } else {
        setInternalViewMode(mode);
      }
      if (persistConfig) {
        saveLayout(buildListConfig({ defaultViewMode: mode }));
      }
    },
    [isViewModeControlled, onViewModeChange, persistConfig, saveLayout, buildListConfig],
  );

  // Handle column resize start
  const handleResizeStart = useCallback((e: React.MouseEvent, columnId: string) => {
    e.preventDefault();
    e.stopPropagation();

    const th = columnRefs.current.get(columnId);
    if (!th) return;

    const currentWidth = th.getBoundingClientRect().width;
    setResizingColumn(columnId);
    setResizeStartX(e.clientX);
    setResizeStartWidth(currentWidth);
  }, []);

  // Track pending save after resize
  const [pendingSave, setPendingSave] = useState(false);

  // Handle column resize move
  useEffect(() => {
    if (!resizingColumn) return;

    const handleMouseMove = (e: MouseEvent) => {
      const delta = e.clientX - resizeStartX;
      const newWidth = Math.max(MIN_COLUMN_WIDTH, resizeStartWidth + delta);
      updateColumnWidth(resizingColumn, newWidth);
    };

    const handleMouseUp = () => {
      setResizingColumn(null);
      setPendingSave(true);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    // Add cursor style to body during resize
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [resizingColumn, resizeStartX, resizeStartWidth, updateColumnWidth]);

  // Auto-save layout after resize ends (with debounce).
  // Hub Krok 2: dla tableId widoku SZABLONU (`template:...`) auto-zapis jest twardo
  // wyłączony — każdy zapis configu szablonu bumpuje wersję techniczną i robi snapshot
  // struktury, więc zapis jedzie WYŁĄCZNIE na żądanie (przyciski Save w panelach).
  useEffect(() => {
    if (!pendingSave || !persistConfig || isTemplateTableId(tableId)) return;

    const timeoutId = setTimeout(() => {
      setPendingSave(false);
      saveLayout(buildListConfig());
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [pendingSave, persistConfig, tableId, saveLayout, buildListConfig]);

  // Get column config by ID
  const getColumnConfig = useCallback(
    (columnId: string): ColumnConfig | undefined => {
      return columnConfigs.find((c) => c.id === columnId);
    },
    [columnConfigs],
  );

  // Get cell value
  const getCellValue = useCallback((row: T, column: ColumnDefinition<T>): unknown => {
    if (typeof column.accessor === 'function') {
      return column.accessor(row);
    }
    return (row as Record<string, unknown>)[column.accessor];
  }, []);

  // Ordered visible columns
  const orderedVisibleColumns = useMemo(() => {
    return [...visibleColumns].sort((a, b) => {
      const configA = getColumnConfig(a.id);
      const configB = getColumnConfig(b.id);
      return (configA?.order ?? 0) - (configB?.order ?? 0);
    });
  }, [visibleColumns, getColumnConfig]);

  // Powiadomienie konsumenta o zmianie efektywnego zbioru widocznych kolumn
  // (`onVisibleColumnsChange`) — WŁĄCZNIE z pierwszym rozwiązaniem po zamontowaniu.
  // `orderedVisibleColumns` jest NOWĄ tablicą co render (spread+sort), więc porównanie
  // referencją w deps odpaliłoby efekt na każdy render — porównujemy TREŚĆ względem
  // ostatnio zgłoszonego zbioru (ref) i wołamy callback tylko gdy realnie się zmienił.
  const lastVisibleColumnIdsRef = useRef<string[] | null>(null);
  useEffect(() => {
    if (!onVisibleColumnsChange) return;
    const ids = orderedVisibleColumns.map((c) => c.id);
    const prev = lastVisibleColumnIdsRef.current;
    const unchanged =
      prev !== null && prev.length === ids.length && prev.every((id, i) => id === ids[i]);
    if (unchanged) return;
    lastVisibleColumnIdsRef.current = ids;
    onVisibleColumnsChange(ids);
  }, [orderedVisibleColumns, onVisibleColumnsChange]);

  // Krok 7.2.5 — pre-compute context dla formattingRules. Memoize per
  // (data, rules) — rekalkulacja przy zmianie zbioru wierszy lub reguł.
  const getValueForFormatting = useCallback(
    (row: T, columnId: string): unknown => {
      const col = columns.find((c) => c.id === columnId);
      if (!col) return undefined;
      return getCellValue(row, col);
    },
    [columns, getCellValue],
  );

  const formattingContext = useMemo(
    () => precomputeRuleContext(data, formattingRules, getValueForFormatting),
    [data, formattingRules, getValueForFormatting],
  );

  // Filter and sort data locally (when not using external handlers)
  const processedData = useMemo(() => {
    let result = [...data];

    // Apply filters (only when using local filters)
    if (!hasExternalFilters && Object.keys(columnFilters).length > 0) {
      result = result.filter((row) => {
        return Object.entries(columnFilters).every(([columnId, filterValue]) => {
          if (filterValue === null || filterValue === '') return true;

          const column = columns.find((c) => c.id === columnId);
          if (!column) return true;

          const cellValue = getCellValue(row, column);
          const cellStr = (
            typeof cellValue === 'object' && cellValue !== null
              ? JSON.stringify(cellValue)
              : String(cellValue ?? '')
          ).toLowerCase();
          const filterStr = String(filterValue).toLowerCase();

          return cellStr.includes(filterStr);
        });
      });
    }

    // Apply header filters lokalnie (gdy nie ma external handlera — wtedy
    // callsite ich nie aplikuje, więc robimy to lokalnie na pobranych
    // rekordach).
    if (!hasExternalHeaderFilters && Object.keys(headerFilters).length > 0) {
      result = result.filter((row) => {
        for (const [columnId, filter] of Object.entries(headerFilters)) {
          const column = columns.find((c) => c.id === columnId);
          if (!column) continue;
          if (
            !passesHeaderFilter(getCellValue(row, column), filter, column.headerFilter?.category)
          ) {
            return false;
          }
        }
        return true;
      });
    }

    // Apply sorting (only when using local sorting)
    if (!hasExternalSort && sortColumn) {
      const column = columns.find((c) => c.id === sortColumn);
      if (column) {
        result.sort((a, b) => {
          const aValue = getCellValue(a, column);
          const bValue = getCellValue(b, column);
          return compareCellValues(aValue, bValue, column, sortDirection);
        });
      }
    }

    return result;
  }, [
    data,
    columnFilters,
    headerFilters,
    sortColumn,
    sortDirection,
    columns,
    getCellValue,
    hasExternalFilters,
    hasExternalHeaderFilters,
    hasExternalSort,
  ]);

  // „Zaznacz wszystko" liczy się po spłaszczonych wierszach grup w trybie grupowanym
  // (patrz `groupedRowsFlat` wyżej), po `processedData` w trybie płaskim — bez zmian.
  const selectAllData = isGroupedMode ? groupedRowsFlat : processedData;

  // Check if any filters are active (zwykłe per-kolumna + header)
  const hasActiveFilters =
    Object.keys(columnFilters).length > 0 || Object.keys(headerFilters).length > 0;

  // Błąd wczytania listy (prop `loadError`, patrz JSDoc w types.ts) — lista NIE udaje pustej.
  // `null`/`undefined` = brak błędu; bez propa oba wyliczenia niżej są `null` (zero zmian).
  const loadErrorKind: ListLoadErrorKind | null =
    loadError != null ? classifyListLoadError(loadError) : null;
  // „Są wczytane wiersze" liczymy po SUROWYM wejściu (nie po lokalnie przefiltrowanym
  // `processedData`): w trybie grupowanym po koszykach (`data` bywa tam celowo puste).
  const hasLoadedRows = groups !== undefined ? groups.length > 0 : data.length > 0;
  const isCalendarView = !isGroupedMode && effectiveViewMode === 'calendar';
  // W MIEJSCU wierszy (zamiast pustego stanu) — gdy nie ma czego pokazać.
  const inPlaceLoadErrorKind: ListLoadErrorKind | null = hasLoadedRows ? null : loadErrorKind;
  // NAD widokiem danych — błąd ponownego pobrania TEGO SAMEGO zapytania (wiersze zostają), a w
  // kalendarzu zawsze: siatka jest stale zamontowana (patrz gałąź kalendarza niżej), więc
  // komunikat nie może zająć jej miejsca.
  const bannerLoadErrorKind: ListLoadErrorKind | null =
    loadErrorKind !== null && !isLoading && (hasLoadedRows || isCalendarView)
      ? loadErrorKind
      : null;

  // Handle save configuration
  const handleSaveConfig = useCallback(async () => {
    if (persistConfig) {
      await saveLayout(buildListConfig());
    }
    setShowConfigModal(false);
  }, [persistConfig, saveLayout, buildListConfig]);

  // Handle select all (uses processedData to only select visible/filtered rows)
  const handleSelectAll = useCallback(
    (checked: boolean, filteredData: T[]) => {
      if (checked) {
        onSelectionChange?.(filteredData.map(getRowId));
      } else {
        onSelectionChange?.([]);
      }
    },
    [getRowId, onSelectionChange],
  );

  // Handle row select
  const handleRowSelect = useCallback(
    (rowId: string, checked: boolean) => {
      if (checked) {
        onSelectionChange?.([...selectedIds, rowId]);
      } else {
        onSelectionChange?.(selectedIds.filter((id) => id !== rowId));
      }
    },
    [selectedIds, onSelectionChange],
  );

  // Render sort icon
  const renderSortIcon = (columnId: string, isSortable: boolean) => {
    if (!isSortable) return null;

    if (sortColumn !== columnId) {
      return (
        <ArrowUpDown className="w-4 h-4 text-text-muted opacity-0 group-hover:opacity-100 transition-opacity" />
      );
    }
    return sortDirection === 'asc' ? (
      <ArrowUp className="w-4 h-4 text-accent-600" />
    ) : (
      <ArrowDown className="w-4 h-4 text-accent-600" />
    );
  };

  // Calculate total columns for colspan
  const totalColumns =
    orderedVisibleColumns.length +
    (showOrdinalColumn ? 1 : 0) +
    (showSelectColumn ? 1 : 0) +
    (showActionsColumn ? 1 : 0);

  // Renderuje pojedynczy wiersz danych — WSPÓLNE dla trybu płaskiego i grupowanego
  // (grupowanie server-side, `groups` prop): grupowany tryb woła TĘ SAMĄ funkcję,
  // włącznie z kolumną zaznaczenia/akcji (`showSelectColumn`/`showActionsColumn`
  // zależą wyłącznie od `selectable`/`renderActions`, nie od trybu). Jedna funkcja
  // renderująca `<tr>` — bez duplikacji.
  const renderDataRow = (row: T, rowIndex: number) => {
    const rowId = getRowId(row);
    const isSelected = selectedIds.includes(rowId);

    // Krok 7.2.5 — agreguj rowStyle ze wszystkich reguł applyTo='row' dotyczących
    // dowolnej kolumny. Każda reguła ewaluowana per kolumna; rowStyle z evaluatora
    // aplikujemy do <tr>.
    let aggregatedRowStyle: React.CSSProperties = {};
    if (formattingRules.length > 0) {
      for (const col of orderedVisibleColumns) {
        const result = evaluateFormattingRules({
          row,
          rowIndex,
          columnId: col.id,
          rules: formattingRules,
          context: formattingContext,
          getValue: getValueForFormatting,
          allRows: data,
        });
        aggregatedRowStyle = { ...aggregatedRowStyle, ...result.rowStyle };
      }
    }

    return (
      <tr
        key={rowId}
        data-testid={testIdPrefix ? `${testIdPrefix}-row-${rowId}` : undefined}
        className={`
                      ${onRowClick ? 'cursor-pointer hover:bg-bg-secondary dark:hover:bg-bg-tertiary' : ''}
                      ${striped && rowIndex % 2 === 1 ? 'bg-bg-secondary dark:bg-bg-secondary/40' : ''}
                      ${isSelected ? 'bg-[rgb(var(--color-surface-selected))]' : ''}
                      ${rowClassName?.(row) || ''}
                    `}
        style={aggregatedRowStyle}
        onClick={() => onRowClick?.(row)}
        onContextMenu={onRowContextMenu ? (e) => onRowContextMenu(e, row) : undefined}
      >
        {/* Selection checkbox */}
        {showSelectColumn && (
          <td
            className={`w-12 px-4 ${compact ? 'py-2' : 'py-3.5'}`}
            onClick={(e) => e.stopPropagation()}
          >
            <input
              type="checkbox"
              checked={isSelected}
              onChange={(e) => handleRowSelect(rowId, e.target.checked)}
              className="rounded border-border-primary text-accent-600 focus:ring-accent-500"
              data-testid={testIdPrefix ? `${testIdPrefix}-row-${rowId}-select` : undefined}
            />
          </td>
        )}

        {/* Lp. — liczba porządkowa wg pozycji na stronie (parytet z Figmą). */}
        {showOrdinalColumn && (
          <td
            className={`w-12 px-4 ${compact ? 'py-2' : 'py-3.5'} text-sm text-text-tertiary`}
            data-testid={testIdPrefix ? `${testIdPrefix}-row-${rowId}-ordinal` : undefined}
          >
            {page * pageSize + rowIndex + 1}
          </td>
        )}

        {/* Data cells */}
        {orderedVisibleColumns.map((column) => {
          const value = getCellValue(row, column);
          const config = getColumnConfig(column.id);
          const cellWidth = config?.width || column.defaultWidth;

          // Krok 7.2.5 — ewaluuj reguły dla tej komórki.
          const fmtResult =
            formattingRules.length > 0
              ? evaluateFormattingRules({
                  row,
                  rowIndex,
                  columnId: column.id,
                  rules: formattingRules,
                  context: formattingContext,
                  getValue: getValueForFormatting,
                  allRows: data,
                })
              : null;

          return (
            <td
              key={column.id}
              className={`px-4 ${compact ? 'py-2' : 'py-3.5'} overflow-hidden text-ellipsis text-sm text-text-primary ${
                column.align === 'right'
                  ? 'text-right'
                  : column.align === 'center'
                    ? 'text-center'
                    : 'text-left'
              } ${bordered ? 'border-r border-border-secondary last:border-r-0' : ''}`}
              style={{
                width: typeof cellWidth === 'number' ? `${cellWidth}px` : cellWidth,
                maxWidth: typeof cellWidth === 'number' ? `${cellWidth}px` : cellWidth,
                ...(fmtResult?.cellStyle ?? {}),
              }}
            >
              <div
                className={wrapCells ? 'line-clamp-2 whitespace-normal break-words' : 'truncate'}
              >
                {column.renderCell ? column.renderCell(value, row, rowIndex) : String(value ?? '-')}
              </div>
            </td>
          );
        })}

        {/* Actions */}
        {showActionsColumn && renderActions && (
          <td
            className={`px-4 ${compact ? 'py-2' : 'py-3.5'} whitespace-nowrap text-right text-sm ${
              bordered ? 'border-r border-border-secondary' : ''
            } ${pinActionsColumn ? PINNED_BODY_CELL_CLASS : ''}`}
            data-testid={testIdPrefix ? `${testIdPrefix}-row-${rowId}-actions-cell` : undefined}
            data-actions-pinned={pinActionsColumn ? 'true' : undefined}
            onClick={(e) => e.stopPropagation()}
          >
            {renderActions(row, TABLE_ACTIONS_CONTEXT)}
          </td>
        )}
      </tr>
    );
  };

  // Stan BŁĘDU wczytania danych w miejscu wierszy — JEDNA ścieżka renderu dla obu wejść:
  // `errorState` (treść dostarcza ekran) i `loadError` (wbudowana klasyfikacja przyczyny +
  // „Spróbuj ponownie", patrz `renderLoadErrorState`). Ton `error`, tytuł i opis od wołającego
  // zamiast „Brak wyników" / „Brak danych" (puste dane nie są tu wynikiem filtra), a przy
  // aktywnych filtrach OBOK `action` także „Wyczyść filtry" — filtr bywa przyczyną błędu, więc
  // użytkownik może go zdjąć albo ponowić to samo zapytanie.
  const renderErrorContent = (state: {
    icon?: React.ComponentType<{ className?: string }>;
    title: string;
    description?: string;
    action?: React.ReactNode;
  }) => (
    <TableEmpty
      icon={state.icon}
      tone="error"
      title={state.title}
      // `''` (nie brak): domyślny opis pustego stanu („Brak rekordów…") nie pasuje do błędu.
      description={state.description ?? ''}
      action={
        hasActiveFilters ? (
          <div className="flex flex-wrap items-center justify-center gap-2">
            <Button variant="tertiary" size="sm" onClick={clearAllFilters}>
              {t('ui.configurableTable.clearFilters')}
            </Button>
            {state.action}
          </div>
        ) : (
          state.action
        )
      }
    />
  );

  // Treść pustego stanu — WSPÓLNA dla tabeli (płaskiej i grupowanej) oraz kart/Kanbana (bez
  // duplikacji treści/przycisku „wyczyść filtry"). Z `errorState` to stan błędu
  // (`renderErrorContent`). Bez `errorState` zachowanie bez zmian.
  const renderEmptyContent = () =>
    errorState ? (
      renderErrorContent({
        icon: emptyIcon,
        title: errorState.title,
        description: errorState.description,
        action: errorState.action,
      })
    ) : (
      <TableEmpty
        icon={emptyIcon}
        tone={hasActiveFilters ? 'search' : 'neutral'}
        title={hasActiveFilters ? t('ui.configurableTable.noResults') : emptyTitle}
        description={
          hasActiveFilters ? t('ui.configurableTable.noResultsDescription') : emptyDescription
        }
        action={
          hasActiveFilters ? (
            <Button variant="tertiary" size="sm" onClick={clearAllFilters}>
              {t('ui.configurableTable.clearFilters')}
            </Button>
          ) : (
            emptyAction
          )
        }
      />
    );

  // Wiersz pustego stanu — WSPÓLNY dla trybu płaskiego i grupowanego. Renderowany, gdy brak
  // wierszy / brak koszyków.
  const renderEmptyRow = () => (
    <tr>
      <td colSpan={totalColumns} data-testid={testIdPrefix ? `${testIdPrefix}-empty` : undefined}>
        {renderFullWidth(renderEmptyContent())}
      </td>
    </tr>
  );

  // Komunikat błędu wczytania listy — jeden tekst per przyczyna (klucze statyczne, żeby
  // bramka i18n je widziała).
  const listLoadErrorMessage = (kind: ListLoadErrorKind): string => {
    switch (kind) {
      case 'badRequest':
        return t('common.lists.loadError.badRequest');
      case 'forbidden':
        return t('common.lists.loadError.forbidden');
      case 'moduleInactive':
        return t('common.lists.loadError.moduleInactive');
      case 'unavailable':
        return t('common.lists.loadError.unavailable');
    }
  };

  // „Spróbuj ponownie" — tylko przy awarii i tylko gdy wołający dał sposób na ponowienie.
  const renderLoadErrorRetry = (kind: ListLoadErrorKind) =>
    isRetryableListLoadError(kind) && onRetryLoad ? (
      <Button
        variant="secondary"
        size="sm"
        onClick={onRetryLoad}
        loading={isFetching}
        data-testid={testIdPrefix ? `${testIdPrefix}-load-error-retry` : undefined}
      >
        {t('common.retry')}
      </Button>
    ) : undefined;

  // Stan błędu W MIEJSCU wierszy dla `loadError` — WSPÓLNY dla tabeli (płaskiej/grupowanej) i
  // kart/Kanbana; renderuje się tą samą ścieżką co `errorState` (`renderErrorContent`), więc przy
  // aktywnych filtrach także oferuje „Wyczyść filtry" obok „Spróbuj ponownie". Bez własnego
  // `role="alert"` na opakowaniu: ton `error` pustego stanu sam ogłasza się czytnikom ekranu
  // (`TableEmpty`), a zagnieżdżone regiony `alert` dublowałyby komunikat.
  const renderLoadErrorState = (kind: ListLoadErrorKind) => (
    <div data-testid={testIdPrefix ? `${testIdPrefix}-load-error` : undefined}>
      {renderErrorContent({
        icon: AlertTriangle,
        title: listLoadErrorMessage(kind),
        action: renderLoadErrorRetry(kind),
      })}
    </div>
  );

  // Wiersz tabeli bez danych: błąd wczytania (gdy jest) albo zwykły pusty stan.
  const renderNoRowsRow = () =>
    inPlaceLoadErrorKind !== null ? (
      <tr>
        <td colSpan={totalColumns}>
          {renderFullWidth(renderLoadErrorState(inPlaceLoadErrorKind))}
        </td>
      </tr>
    ) : (
      renderEmptyRow()
    );

  // Show loading skeleton while layout is loading
  if (layoutLoading) {
    return (
      <TableLoading rows={5} columns={columns.filter((c) => c.defaultVisible !== false).length} />
    );
  }

  // Treść wiersza PODSUMOWANIA: jedno wywołanie `footerCells` na WIDOCZNĄ kolumnę (kolumny ukryte
  // z menu kolumn albo przez układ nie liczą się). Z etykietą (`footerLabel`) wiersz, w którym
  // żadna komórka nie ma treści, to goła „Razem" bez ani jednej sumy — nie rysujemy go. Bez
  // etykiety zachowanie jak dotąd.
  const footerContents: React.ReactNode[] | null =
    footerCells && !isLoading ? orderedVisibleColumns.map((column) => footerCells(column)) : null;
  const shownFooterContents =
    footerContents !== null &&
    (footerLabel === undefined || footerContents.some((content) => hasFooterContent(content)))
      ? footerContents
      : null;

  return (
    <div
      ref={rootRef}
      className={`relative rounded-3xl border border-[rgba(103,103,103,0.3)] bg-white shadow-[0_6px_45px_rgba(127,145,164,0.1)] dark:border-gray-700 dark:bg-gray-800 ${
        fillHeight ? 'flex h-full min-h-0 flex-col' : ''
      } ${className}`}
    >
      {/* Górny pasek listy (chipy grup + baton odśwież/filtry) — WEWNĄTRZ karty, nad
          nagłówkiem. Bez kreski oddzielającej (wg Figmy nagłówek nie ma linii nad tytułami). */}
      {listToolbar && <div className="px-7 pb-4 pt-3">{listToolbar}</div>}

      {/* Bulk Actions Bar — działa też w trybie grupowanym (selekcja jest globalna,
          niezależna od podziału na koszyki). */}
      {selectable && selectedIds.length > 0 && renderBulkActions && (
        <div className="flex items-center gap-4 px-4 py-2 bg-accent-50 border-b border-accent-200">
          <span role="status" aria-live="polite" className="text-sm font-medium text-accent-800">
            {t('ui.configurableTable.selectedCount', { count: selectedIds.length })}
          </span>
          {renderBulkActions(selectedIds)}
        </div>
      )}

      {/* Table Actions Bar — selektor layoutu (gdy >1) po lewej + `toolbarExtra`
          (dowolna zawartość od wołającego) i przełącznik trybu widoku (lista / karty /
          Kanban) po prawej, w jednej belce. Konfiguracja kolumn przeniesiona do Kreatora
          Listy (zakładka Kolumny). W trybie sterowanym przełącznik renderuje strona w
          swoim nagłówku — wbudowany pomijamy, chyba że trzeba pokazać selektor layoutu
          albo wołający podał `toolbarExtra`. */}
      {(showInternalViewModeToggle ||
        (configurable && availableLayouts.length > 1) ||
        toolbarExtra) && (
        <div className="flex items-center justify-between gap-2 px-4 py-2 border-b border-border-secondary bg-bg-secondary">
          {configurable && availableLayouts.length > 1 ? (
            <Dropdown
              options={availableLayouts.map((l) => ({ value: l.id, label: l.name }))}
              value={layout?.id || ''}
              onChange={(val) => switchLayout(val)}
              className="w-auto"
            />
          ) : (
            <span />
          )}

          <div className="flex items-center gap-2">
            {toolbarExtra}

            {/* Przełącznik trybu widoku (tylko gdy nie sterowany i nie wyłączony) */}
            {showInternalViewModeToggle && (
              <div
                className="inline-flex rounded-md border border-border-primary bg-bg-elevated p-0.5"
                role="group"
                data-testid={testIdPrefix ? `${testIdPrefix}-viewmode` : undefined}
              >
                {visibleViewModes.map(({ mode, Icon }) => {
                  const active = effectiveViewMode === mode;
                  const label = t(`tenant.listBuilder.viewMode.${mode}`);
                  return (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => handleViewModeChange(mode)}
                      aria-pressed={active}
                      aria-label={label}
                      title={label}
                      data-testid={testIdPrefix ? `${testIdPrefix}-viewmode-${mode}` : undefined}
                      className={`flex items-center justify-center rounded p-1.5 transition-colors ${
                        active
                          ? 'bg-accent-100 text-accent-700 dark:bg-accent-900/40 dark:text-accent-300'
                          : 'text-text-muted hover:text-text-secondary'
                      }`}
                    >
                      <Icon className="w-4 h-4" aria-hidden="true" />
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Błąd wczytania listy NAD widokiem danych (odświeżenie nieudane przy zachowanych
          wierszach; w kalendarzu zawsze) — patrz `bannerLoadErrorKind`. */}
      {bannerLoadErrorKind !== null && (
        <div className="shrink-0 px-7 pb-2 pt-2">
          <Alert
            tone="error"
            title={listLoadErrorMessage(bannerLoadErrorKind)}
            actions={renderLoadErrorRetry(bannerLoadErrorKind)}
            data-testid={testIdPrefix ? `${testIdPrefix}-load-error-banner` : undefined}
          />
        </div>
      )}

      {/* Pasek widgetów (KPI) przeniesiony na GÓRĘ strony (nad kategorie/filtry)
          przez `ListTopWidgets` montowany w komponencie strony — patrz decyzja
          2026-06-03. Tu już go nie renderujemy. */}

      {/* Widok danych: tabela / karty / Kanban. Tryb grupowany (`groups` podane) POMIJA
          `effectiveViewMode` — renderuje się zawsze jako lista (koszyki + wiersze),
          niezależnie od wybranego trybu karty/Kanban. */}
      <div
        className={
          fillHeight
            ? 'min-h-0 flex-1 overflow-hidden rounded-b-lg px-7 pb-3 pt-1'
            : 'px-7 pb-3 pt-1'
        }
      >
        {!isGroupedMode && effectiveViewMode === 'calendar' ? (
          // Siatka kalendarza jest ZAWSZE zamontowana w trybie kalendarza — także na czas
          // wczytywania. Jej odmontowanie zeruje zakres miesiąca w zapytaniu listy
          // (`onRangeChange(null)`), klucz zapytania wraca do wariantu bez zakresu i zapytanie
          // leci od nowa; przy stałym błędzie serwera to była pętla (wczytanie → odmontowanie →
          // nowe zapytanie → …). Wczytywanie pokazujemy nakładką NA siatce.
          <div className={fillHeight ? 'relative h-full' : 'relative'}>
            <CalendarBoard<T>
              columns={orderedVisibleColumns as ColumnDefinition<T>[]}
              /* Pola kalendarza z WSZYSTKICH definicji kolumn strony — kalendarz nie zależy od
                 tego, które kolumny są widoczne na liście (wartości ukrytych kolumn dociąga strona
                 przez `?columns=`, patrz `computeVisibleColumnParams`). */
              fieldColumns={columns}
              data={processedData}
              getRowId={getRowId}
              getCellValue={getCellValue}
              getColumnConfig={getColumnConfig}
              calendarConfig={layout?.layoutConfig?.calendarConfig}
              titleColumnId={titleColumnId}
              onRowClick={onRowClick}
              onRangeChange={onCalendarRangeChange}
              /* Licznik serwera — gdy miesiąc ma więcej wpisów niż wczytano jednym zapytaniem,
                 siatka pokazuje komunikat „pokazano X z Y". Tylko przy zapytaniu zawężonym do
                 miesiąca (`onCalendarRangeChange`) — inaczej licznik nie dotyczy miesiąca. */
              totalItems={onCalendarRangeChange ? totalItems : undefined}
              fillHeight={fillHeight}
              /* Tłumi komunikaty „pusty miesiąc" / „kalendarz nieskonfigurowany" na czas
                 wczytywania oraz przy błędzie wczytania — wtedy powód braku wpisów mówi
                 komunikat błędu nad siatką. */
              loading={isLoading || isFetching || loadErrorKind !== null}
              testIdPrefix={testIdPrefix}
            />
            {isLoading && (
              // `pointer-events-none` — nakładka tylko pokazuje wskaźnik; nawigacja miesiąca pod
              // nią działa (klik „następny miesiąc" w trakcie wczytywania zmienia zakres).
              <div
                className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-white/60 dark:bg-gray-800/60"
                data-testid={testIdPrefix ? `${testIdPrefix}-calendar-loading` : undefined}
              >
                <TableLoading inline />
              </div>
            )}
          </div>
        ) : !isGroupedMode && (effectiveViewMode === 'cards' || effectiveViewMode === 'kanban') ? (
          isLoading ? (
            <div className="p-4">
              <TableLoading inline />
            </div>
          ) : inPlaceLoadErrorKind !== null ? (
            <div className="p-4">{renderLoadErrorState(inPlaceLoadErrorKind)}</div>
          ) : processedData.length === 0 ? (
            <div className="p-4">{renderEmptyContent()}</div>
          ) : effectiveViewMode === 'cards' ? (
            <CardsView
              columns={orderedVisibleColumns as ColumnDefinition<T>[]}
              data={processedData}
              getRowId={getRowId}
              getCellValue={getCellValue}
              getColumnConfig={getColumnConfig}
              onRowClick={onRowClick}
              renderActions={renderActions}
              titleColumnId={titleColumnId}
              selectable={selectable}
              selectedIds={selectedIds}
              onRowSelect={handleRowSelect}
              testIdPrefix={testIdPrefix}
            />
          ) : (
            <KanbanBoard
              columns={orderedVisibleColumns as ColumnDefinition<T>[]}
              data={processedData}
              getRowId={getRowId}
              getCellValue={getCellValue}
              getColumnConfig={getColumnConfig}
              kanbanConfig={layout?.layoutConfig?.kanbanConfig}
              onRowClick={onRowClick}
              onRowUpdate={onRowUpdate}
              canMoveField={canMoveKanbanField}
              renderActions={renderActions}
              testIdPrefix={testIdPrefix}
            />
          )
        ) : (
          <>
            {/* Table */}
            <div
              ref={setScrollElement}
              className={fillHeight ? 'h-full overflow-auto' : 'overflow-x-auto'}
            >
              <table
                ref={setTableElement}
                className="min-w-full"
                // UWAGA: tabela nie ma jawnej szerokości (tylko `min-w-full`, czyli `width: auto`),
                // więc przeglądarka liczy układ AUTOMATYCZNY mimo `tableLayout: 'fixed'` — kolumna
                // akcji rośnie do treści komórek (np. przyciski w jednej linii), a nie zostaje przy
                // `actionsColumnWidth`. Nadanie tabeli jawnej szerokości (`width`, `w-full` itp.)
                // włączyłoby układ stały i zepchnęło kolumnę akcji do `actionsColumnWidth`.
                style={{ tableLayout: 'fixed' }}
                data-skin={tableSkin}
                data-testid={testIdPrefix ? `${testIdPrefix}-table` : undefined}
              >
                <thead
                  className={`bg-bg-tertiary [&>tr:first-child>th:first-child]:rounded-tl-[14px] [&>tr:first-child>th:last-child]:rounded-tr-[14px] [&>tr:last-child>th:first-child]:rounded-bl-[14px] [&>tr:last-child>th:last-child]:rounded-br-[14px] dark:bg-gray-900 ${stickyHeader ? 'sticky top-0 z-10' : ''}`}
                >
                  <tr>
                    {/* Selection checkbox column. */}
                    {showSelectColumn && (
                      <th className="h-11 w-12 px-4">
                        {/* „Zaznacz wszystko" ukryte w trybie grupowanym z leniwym doładowaniem
                            (`isControlledExpand`): `selectAllData` = spłaszczone `groups[].rows`
                            obejmuje wtedy tylko koszyki ROZWINIĘTE i doładowane (zwinięte niosą
                            `rows: []`), więc checkbox zaznaczałby mylący podzbiór (i tylko bieżącą
                            stronę mini-paginacji koszyka). Zaznaczanie pojedynczych wierszy w
                            rozwiniętym koszyku działa dalej. Tryb grupowany BEZ sterowania (eager
                            `rows`) ma komplet wierszy — tam checkbox zostaje (patrz test TC6). */}
                        {!isControlledExpand && (
                          <input
                            type="checkbox"
                            checked={
                              selectedIds.length === selectAllData.length &&
                              selectAllData.length > 0
                            }
                            ref={(el) => {
                              if (el) {
                                el.indeterminate =
                                  selectedIds.length > 0 &&
                                  selectedIds.length < selectAllData.length;
                              }
                            }}
                            onChange={(e) => handleSelectAll(e.target.checked, selectAllData)}
                            className="rounded border-border-primary text-accent-600 focus:ring-accent-500"
                          />
                        )}
                      </th>
                    )}

                    {/* Kolumna „Lp." — nagłówek liczby porządkowej. */}
                    {showOrdinalColumn && (
                      <th className="h-11 w-12 px-4 text-left text-[11px] font-semibold uppercase tracking-[0.6px] text-text-muted">
                        {t('ui.configurableTable.ordinalHeader')}
                      </th>
                    )}

                    {/* Data columns */}
                    {orderedVisibleColumns.map((column, colIndex) => {
                      const config = getColumnConfig(column.id);
                      const label = config?.customLabel || column.header;
                      const width = config?.width || column.defaultWidth;
                      const isSortable = column.sortable !== false;
                      const isLastColumn =
                        colIndex === orderedVisibleColumns.length - 1 && !showActionsColumn;

                      return (
                        <th
                          key={column.id}
                          ref={(el) => {
                            if (el) columnRefs.current.set(column.id, el);
                          }}
                          className={`group relative h-11 whitespace-nowrap px-4 text-left text-[11px] font-semibold uppercase tracking-[0.6px] text-text-muted ${
                            isSortable ? 'cursor-pointer hover:bg-bg-secondary' : ''
                          } ${bordered ? 'border-r border-border-secondary last:border-r-0' : ''}`}
                          style={{
                            width: typeof width === 'number' ? `${width}px` : width || undefined,
                            minWidth: column.minWidth || MIN_COLUMN_WIDTH,
                            maxWidth: column.maxWidth,
                          }}
                          onClick={() => {
                            if (isSortable) {
                              const newDirection =
                                sortColumn === column.id && sortDirection === 'asc'
                                  ? 'desc'
                                  : 'asc';
                              handleSort(column.id, newDirection);
                            }
                          }}
                          onContextMenu={(e) => {
                            e.preventDefault();
                            setContextMenuColumnId(column.id);
                            setContextMenuAnchor({ x: e.clientX, y: e.clientY });
                          }}
                        >
                          <div
                            className={`flex items-center gap-2 ${
                              column.align === 'right'
                                ? 'justify-end'
                                : column.align === 'center'
                                  ? 'justify-center'
                                  : ''
                            }`}
                          >
                            {column.renderHeader ? column.renderHeader(config!) : label}
                            {renderSortIcon(column.id, isSortable)}

                            {/* Ikonka filtra (po naciśnięciu otwiera popup z 3
                          trybami). Pokazuje się tylko gdy kolumna ma
                          zdefiniowany headerFilter z category !== 'none'.
                          `boolean` WYŁĄCZONE jawnie — tri-state w wierszu filtrów
                          (niżej) w pełni pokrywa filtrowanie boolean; popup dla tej
                          kategorii ma tylko zakładkę "Wartości" (`ColumnHeaderFilterPopup`:
                          `showConditionTab`/`showRangeTab` false dla boolean) i pisze do
                          TEGO SAMEGO `headerFilters[columnId]`, więc dwie niesynchronizowane
                          ścieżki zapisu tego samego wpisu: filtr `mode:'values'` z popupu
                          byłby niewidoczny dla `readBooleanFilterState` (rozumie wyłącznie
                          `mode:'condition'`+`operator:'eq'` → pokazywałby "Wszystkie" mimo
                          aktywnego filtra), a warunek ustawiony z tri-state otwierałby popup
                          na osieroconej zakładce "Warunek" (przycisk zakładki niedostępny dla
                          boolean, ale treść zakładki renderuje się bezwarunkowo po
                          `activeTab==='condition'`, który tri-state mógłby tam zostawić).
                          `record-select` WYŁĄCZONE analogicznie — wiersz filtrów renderuje
                          `<RecordSelect>` (searchable multiselect z auto-selectem), popup
                          duplikowałby tę samą kontrolkę starą listą checkboxów. */}
                            {column.headerFilter &&
                              column.headerFilter.category !== 'none' &&
                              column.headerFilter.category !== 'boolean' &&
                              column.headerFilter.category !== 'record-select' &&
                              // Wygaszona przez grupę OR — blokujemy popup (druga ścieżka zapisu do
                              // `headerFilters[columnId]`), inaczej ominąłby wzajemne wykluczanie.
                              !orGroupDisabled[column.id] && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (filterPopupColumnId === column.id) {
                                      setFilterPopupColumnId(null);
                                      setFilterPopupAnchorRect(null);
                                    } else {
                                      const th = (e.currentTarget as HTMLElement).closest(
                                        'th',
                                      ) as HTMLElement;
                                      const thRect = th.getBoundingClientRect();
                                      const rootRect = rootRef.current?.getBoundingClientRect();
                                      if (!rootRect) return;
                                      setFilterPopupColumnId(column.id);
                                      setFilterPopupAnchorRect({
                                        top: thRect.top - rootRect.top,
                                        left: thRect.left - rootRect.left,
                                        right: thRect.right - rootRect.left,
                                        bottom: thRect.bottom - rootRect.top,
                                      });
                                    }
                                  }}
                                  className={`p-0.5 rounded transition-all ${
                                    headerFilters[column.id]
                                      ? 'text-accent-600 opacity-100'
                                      : 'text-text-muted opacity-0 group-hover:opacity-100 hover:text-text-secondary'
                                  }`}
                                  title={t('ui.configurableTable.filterColumn')}
                                >
                                  <Filter className="w-3.5 h-3.5" />
                                </button>
                              )}
                          </div>

                          {/* Resize handle */}
                          {configurable && !isLastColumn && (
                            <div
                              className="absolute top-0 right-0 w-4 h-full cursor-col-resize flex items-center justify-center group/resize"
                              onMouseDown={(e) => handleResizeStart(e, column.id)}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div
                                className={`w-0.5 h-4 rounded transition-all ${
                                  resizingColumn === column.id
                                    ? 'bg-accent-500 h-full'
                                    : 'bg-transparent group-hover/resize:bg-accent-400 group-hover/resize:h-6'
                                }`}
                              />
                            </div>
                          )}
                        </th>
                      );
                    })}

                    {/* Actions column. */}
                    {showActionsColumn && (
                      <th
                        ref={setActionsHeaderElement}
                        className={`h-11 px-4 text-right text-[11px] font-semibold uppercase tracking-[0.6px] text-text-muted ${
                          bordered ? 'border-r border-border-secondary' : ''
                        } ${pinActionsColumn ? PINNED_HEADER_CELL_CLASS : ''}`}
                        style={{ width: actionsColumnWidth }}
                        data-testid={
                          testIdPrefix ? `${testIdPrefix}-actions-column-header` : undefined
                        }
                        data-actions-pinned={pinActionsColumn ? 'true' : undefined}
                      >
                        {actionsColumnLabel ?? t('ui.configurableTable.actionsHeader')}
                      </th>
                    )}
                  </tr>

                  {/* Filter row */}
                  {filterable && (
                    <tr className="bg-bg-tertiary dark:bg-gray-900">
                      {/* Selection column spacer */}
                      {showSelectColumn && <th className="w-12 px-4 py-2" />}
                      {showOrdinalColumn && <th className="w-12 px-4 py-2" />}

                      {/* Filter inputs for each column — jedna kontrolka per kolumna,
                        dyspozytor `FilterRowCell` (operatory + współdzielone kontrolki). */}
                      {orderedVisibleColumns.map((column) => {
                        const config = getColumnConfig(column.id);
                        const width = config?.width || column.defaultWidth;
                        const columnLabel = config?.customLabel || column.header;

                        return (
                          <th
                            key={`filter-${column.id}`}
                            className={`px-4 py-2 align-top ${bordered ? 'border-r border-border-secondary last:border-r-0' : ''}`}
                            style={{
                              width: typeof width === 'number' ? `${width}px` : width || undefined,
                            }}
                          >
                            <FilterRowCell
                              column={column}
                              columnLabel={String(columnLabel)}
                              filter={headerFilters[column.id]}
                              columnFilterValue={String(columnFilters[column.id] ?? '')}
                              hasExternalHeaderFilters={hasExternalHeaderFilters}
                              hasExternalColumnFilters={hasExternalFilters}
                              onApplyHeaderFilter={commitHeaderFilter}
                              onColumnFilterChange={handleFilterChange}
                              disabled={!!orGroupDisabled[column.id]}
                              echoFilter={orGroupDisabled[column.id]}
                              testIdPrefix={testIdPrefix}
                            />
                          </th>
                        );
                      })}

                      {/* Actions column spacer. */}
                      {showActionsColumn && (
                        <th
                          className={`px-4 py-2 ${bordered ? 'border-r border-border-secondary' : ''} ${
                            pinActionsColumn ? PINNED_HEADER_CELL_CLASS : ''
                          }`}
                          style={{ width: actionsColumnWidth }}
                          data-testid={
                            testIdPrefix ? `${testIdPrefix}-actions-column-filter` : undefined
                          }
                          data-actions-pinned={pinActionsColumn ? 'true' : undefined}
                        >
                          {hasActiveFilters && (
                            <Button
                              variant="tertiary"
                              size="sm"
                              className="whitespace-nowrap"
                              onClick={clearAllFilters}
                            >
                              {t('ui.configurableTable.clearFiltersShort')}
                            </Button>
                          )}
                        </th>
                      )}
                    </tr>
                  )}
                </thead>

                <tbody className="divide-y divide-border-secondary bg-white dark:divide-gray-700 dark:bg-gray-800">
                  {isLoading ? (
                    <tr>
                      <td colSpan={totalColumns}>{renderFullWidth(<TableLoading inline />)}</td>
                    </tr>
                  ) : groups ? (
                    // Tryb grupowany — koszyk (nagłówek + licznik + summary + zwijanie) +
                    // pod nim wiersze koszyka, TYM SAMYM renderowaniem co tryb płaski
                    // (`renderDataRow`, włącznie z kolumną zaznaczenia/akcji, gdy podane).
                    groups.length === 0 ? (
                      renderNoRowsRow()
                    ) : (
                      groups.map((group) => {
                        const collapsed = isGroupCollapsed(group.key);
                        return (
                          <Fragment key={group.key}>
                            <tr
                              className="bg-bg-tertiary dark:bg-bg-secondary/60"
                              data-testid={
                                testIdPrefix ? `${testIdPrefix}-group-${group.key}` : undefined
                              }
                            >
                              <td colSpan={totalColumns}>
                                {/* Padding komórki siedzi na opakowaniu `renderFullWidth` (patrz
                                    komentarz tam) — nazwa, licznik i suma stoją przy lewej krawędzi
                                    WIDOCZNEJ części tabeli, nie uciekają przy przewijaniu w bok. */}
                                {renderFullWidth(
                                  <button
                                    type="button"
                                    onClick={() => handleGroupToggleClick(group.key)}
                                    aria-expanded={!collapsed}
                                    className="flex w-full items-center gap-2 text-left"
                                    data-testid={
                                      testIdPrefix
                                        ? `${testIdPrefix}-group-toggle-${group.key}`
                                        : undefined
                                    }
                                  >
                                    {collapsed ? (
                                      <ChevronRight
                                        className="h-4 w-4 shrink-0 text-text-muted"
                                        aria-hidden="true"
                                      />
                                    ) : (
                                      <ChevronDown
                                        className="h-4 w-4 shrink-0 text-text-muted"
                                        aria-hidden="true"
                                      />
                                    )}
                                    <span className="font-medium text-text-primary">
                                      {group.label}
                                    </span>
                                    <span className="rounded-full bg-bg-tertiary px-2 py-0.5 text-xs font-semibold text-text-secondary">
                                      {group.count}
                                    </span>
                                    {/* Suma koszyka tuż OBOK nazwy/licznika grupy (nie dosunięta do
                                      prawej) — po prawej, przy kolumnie akcji, była słabo widoczna. */}
                                    {group.summary && (
                                      <span className="text-sm font-normal text-text-tertiary">
                                        {group.summary}
                                      </span>
                                    )}
                                  </button>,
                                  compact ? 'px-4 py-2' : 'px-4 py-3',
                                )}
                              </td>
                            </tr>
                            {!collapsed &&
                              (group.isLoading ? (
                                <tr
                                  data-testid={
                                    testIdPrefix
                                      ? `${testIdPrefix}-group-rows-loading-${group.key}`
                                      : undefined
                                  }
                                >
                                  <td colSpan={totalColumns}>
                                    {renderFullWidth(<TableLoading inline />)}
                                  </td>
                                </tr>
                              ) : (
                                group.rows.map((row, rowIndex) => renderDataRow(row, rowIndex))
                              ))}
                            {!collapsed && group.footer && (
                              <tr>
                                <td colSpan={totalColumns}>
                                  {renderFullWidth(
                                    group.footer,
                                    compact ? 'px-4 py-1.5' : 'px-4 py-2',
                                  )}
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        );
                      })
                    )
                  ) : processedData.length === 0 ? (
                    renderNoRowsRow()
                  ) : (
                    processedData.map((row, rowIndex) => renderDataRow(row, rowIndex))
                  )}
                </tbody>

                {/* PODSUMOWANIE — w `<tfoot>`, żeby kwoty stały dokładnie pod swoimi kolumnami
                    niezależnie od ich szerokości, i żeby przy wydruku powtarzało się na stronach.
                    Tabela niczego tu nie liczy: treść komórek podaje wołający. */}
                {shownFooterContents && (
                  <tfoot className="border-t-2 border-border-primary bg-bg-secondary font-medium">
                    <tr data-testid={testIdPrefix ? `${testIdPrefix}-footer` : undefined}>
                      {showSelectColumn && <td className="px-3 py-2" />}
                      {/* Kolumna „Lp." też ma swoją komórkę — bez niej kwoty przesunęłyby się o
                          jedną kolumnę w lewo względem nagłówków. */}
                      {showOrdinalColumn && <td className="px-3 py-2" />}
                      {orderedVisibleColumns.map((column, index) => (
                        <td
                          key={column.id}
                          className={`px-3 py-2 text-sm text-text-primary ${
                            column.align === 'right' ? 'text-right' : ''
                          }`}
                        >
                          {index === 0 && footerLabel !== undefined ? (
                            <FooterLabelCell
                              label={footerLabel}
                              content={shownFooterContents[index]}
                            />
                          ) : (
                            shownFooterContents[index]
                          )}
                        </td>
                      ))}
                      {showActionsColumn && (
                        <td
                          className={`px-3 py-2 ${pinActionsColumn ? PINNED_FOOTER_CELL_CLASS : ''}`}
                          data-testid={
                            testIdPrefix ? `${testIdPrefix}-actions-column-footer` : undefined
                          }
                          data-actions-pinned={pinActionsColumn ? 'true' : undefined}
                        />
                      )}
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </>
        )}
      </div>

      {/* Header filter popup — renderowany w containerze tabeli, pozycja
          obliczona względem rootRef. */}
      {filterPopupColumnId &&
        filterPopupAnchorRect &&
        (() => {
          const popupColumn = orderedVisibleColumns.find((c) => c.id === filterPopupColumnId);
          if (!popupColumn?.headerFilter) return null;
          return (
            <ColumnHeaderFilterPopup
              column={popupColumn as ColumnDefinition<unknown>}
              filterConfig={popupColumn.headerFilter}
              currentFilter={headerFilters[filterPopupColumnId]}
              onApply={(filter) => handleHeaderFilterApply(filterPopupColumnId, filter)}
              onClose={() => {
                setFilterPopupColumnId(null);
                setFilterPopupAnchorRect(null);
              }}
              anchorRect={filterPopupAnchorRect}
              tableId={distinctTableId}
            />
          );
        })()}

      {/* Pagination — pomijana w trybie grupowanym (grupowanie nie paginuje). W trybie kalendarza
          paginacja nie ma sensu (nawigacja idzie miesiącami, a zapytanie jest już zawężone do
          widocznego miesiąca) — pokazujemy sam licznik wyników (`countOnly`). */}
      {!isGroupedMode && totalItems !== undefined && totalItems > 0 && onPageChange && (
        <TablePagination
          page={page}
          pageSize={pageSize}
          totalItems={totalItems}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange ? handleUserPageSizeChange : undefined}
          pageSizeOptions={effectivePageSizeOptions}
          countOnly={effectiveViewMode === 'calendar'}
        />
      )}

      {/* Configuration Modal */}
      {showConfigModal && (
        <ColumnConfigModal
          columns={columns}
          columnConfigs={columnConfigs}
          onConfigChange={setColumnConfigs}
          onSave={handleSaveConfig}
          onReset={resetColumnConfig}
          onClose={() => setShowConfigModal(false)}
          availableLayouts={availableLayouts}
          currentLayoutId={layout?.id}
          onSwitchLayout={switchLayout}
          onCreateLayout={isAdmin ? createLayout : undefined}
          onDeleteLayout={isAdmin ? deleteLayout : undefined}
          isAdmin={isAdmin}
          isSaving={isSaving}
        />
      )}

      {/* Menu kontekstowe nagłówka kolumny (Krok 7.2.1). */}
      <ColumnContextMenu
        open={contextMenuColumnId !== null}
        anchor={contextMenuAnchor}
        isFilterRowVisible={filterable}
        onClose={() => {
          setContextMenuColumnId(null);
          setContextMenuAnchor(null);
        }}
        onCfPresetSelected={(preset) => {
          // Otwiera Rule Editor modal z pre-set'em typu reguły.
          setRuleEditorColumnId(contextMenuColumnId);
          setRuleEditorPreset(preset);
        }}
        onCfManageRules={() => {
          // Krok 7.2.4 — Manager modal.
          setManagerColumnId(contextMenuColumnId);
        }}
        onAction={(action: ColumnContextAction) => {
          const columnId = contextMenuColumnId;
          if (!columnId) return;
          switch (action) {
            case 'sort_asc':
              handleSort(columnId, 'asc');
              break;
            case 'sort_desc':
              handleSort(columnId, 'desc');
              break;
            case 'hide':
              setColumnConfigs(
                columnConfigs.map((c) => (c.id === columnId ? { ...c, visible: false } : c)),
              );
              break;
            case 'column_chooser':
              setShowConfigModal(true);
              break;
            case 'filter_editor': {
              const th = columnRefs.current.get(columnId);
              if (!th) break;
              const thRect = th.getBoundingClientRect();
              const rootRect = rootRef.current?.getBoundingClientRect();
              if (!rootRect) break;
              setFilterPopupColumnId(columnId);
              setFilterPopupAnchorRect({
                top: thRect.top - rootRect.top,
                left: thRect.left - rootRect.left,
                right: thRect.right - rootRect.left,
                bottom: thRect.bottom - rootRect.top,
              });
              break;
            }
            case 'conditional_formatting':
              // Submenu — Krok 7.2.2 doimplementuje. Na razie no-op (menu zostaje otwarte).
              break;
            default:
              // Pozostałe akcje (group_by, split, best_fit*, show_find_panel,
              // toggle_auto_filter_row, rename) — placeholder dla późniejszych
              // kroków lub całkiem osobnych feature'ów.
              break;
          }
        }}
      />

      {/* Modal edytora reguły (Krok 7.2.3). Otwierany z menu kontekstowego
          przez submenu CF — tworzy nową regułę i propaguje do saveLayout. */}
      {ruleEditorColumnId !== null && (
        <FormattingRuleEditorModal
          open={true}
          columnId={ruleEditorColumnId}
          columnLabel={
            columns.find((c) => c.id === ruleEditorColumnId)?.header?.toString() ??
            ruleEditorColumnId
          }
          availableColumns={columns}
          presetType={ruleEditorPreset?.type}
          presetCondition={ruleEditorPreset?.condition}
          onSave={(rule) => {
            saveFormattingRules([...formattingRules, rule]);
            setRuleEditorColumnId(null);
            setRuleEditorPreset(null);
          }}
          onClose={() => {
            setRuleEditorColumnId(null);
            setRuleEditorPreset(null);
          }}
        />
      )}

      {/* Manager Rules modal (Krok 7.2.4). */}
      {managerColumnId !== null && (
        <FormattingRulesManagerModal
          open={true}
          columnId={managerColumnId}
          columnLabel={
            columns.find((c) => c.id === managerColumnId)?.header?.toString() ?? managerColumnId
          }
          availableColumns={columns}
          rules={formattingRules}
          onChange={saveFormattingRules}
          onClose={() => setManagerColumnId(null)}
        />
      )}
    </div>
  );
}

// Kontrolki wiersza filtrów (tri-state boolean, edytory text/number/date, menu
// operatorów, dyspozytor `FilterRowCell`) mieszkają w `./filter-row/*` — testowalne
// wprost, bez montowania całej tabeli. Współdzielone kontrolki (boolean/select/
// choice/data na Dropdown) reużywane z `./filter-row-controls`. Header-filter
// predykat (`passesHeaderFilter`) mieszka w `./header-filter` (bliźniak `./sort-utils`).
